/**
 * 视频模板 —— **全站唯一一份视频模板清单**。
 *
 * ---------------------------------------------------------------------------
 * 为什么有这个文件
 * ---------------------------------------------------------------------------
 * 2026-09-29 用户指出：视频工作台「这里选择的时候需要选模板，模板需要有图片介绍」，
 * 并给了一张参考站截图 —— 一排带封面图与播放量的模板卡，点一张就把整条工作流的
 * 参数铺好。上一版没有任何模板概念：用户进 `/video-studio` 面对的是一张空表单，
 * 得自己从「场景 / 风格 / 运镜 / 时长」四个维度拼出一个方向。
 *
 * 模板承担的是**冷启动**：多数人进来时并不知道自己要什么，只知道「想做一个
 * 别的品牌那种会动的服装」。所以一张模板 = 一组已经配平的参数
 * + **一段自己的镜头脚本** + 一句跨全片的结构约束。
 *
 * ---------------------------------------------------------------------------
 * 四条硬约束（踩过就不敢放松）
 * ---------------------------------------------------------------------------
 * 1. **模板只铺参数，不新增字段**。`preset` 里的键必须逐个命中页面控件已有的取值
 *    （`VIDEO_STYLES` / `VIDEO_DURATIONS` / `VIDEO_RATIOS` 与档位 `scenes`）。
 *    所以这里对 style / ratio 用联合类型收口 —— 写错一个词 tsc 就拦下来，
 *    而不是等到用户点了模板发现下拉框里没高亮。
 *    （`camera` 已于 2026-10-09 按用户口径「去掉运镜这一项」删除。）
 *
 * 2. **选中态是派生的，不是存的**。`activeTemplate()` 拿当前表单和每条模板的
 *    `preset` 逐字段比，全等才算选中。不另存一个 `form.template`：那样用户手改了
 *    风格之后卡片还亮着，等于在骗人。这一条与本项目「能派生的别存成 state」同源。
 *
 * 3. **归属靠 `modes` 声明，页面不写第二份判断**。每条模板用 `modes` 说明它属于哪一档，
 *    `templatesForMode()` 是「本档有哪些模板」的唯一出口，页面与
 *    `scripts/export-template-count.ts` 都从它取。
 *    2026-10-05 并档之后 `modes` 只剩 `short-video` 一个值（`video-replicate` 不挂模板 ——
 *    它的输入物是参考视频，是复刻不是生成），但这一层**不删**：以后真要再分档，
 *    加一条模板的 `modes` 就够了，页面不用回头改。
 *
 * 4. **每条模板必须自带 `storyboard`（必填）**。只铺参数不写分镜的模板是假模板 ——
 *    详见 `VideoTemplate.storyboard` 的注释与 `videoStoryboard()` 的第二参数。
 *
 * 5. **`preset` 的五个字段不许与同档另一条全等**。选中态是 `activeTemplate()`
 *    逐字段比出来的，两条全等时 `find()` 只认头一条，**另一条永远点不亮** ——
 *    用户看到的不是报错，是「点了没反应」。加模板前先扫一遍同档的参数组合：
 *    现有七条已经把「种草氛围 / 高对比潮流 / 推拉运镜 / 8s」「信息流广告 /
 *    清爽电商棚拍 / 推拉运镜 / 8s」「TVC 品牌质 / 极简白底科技感 / 推拉运镜 / 15s」
 *    这类组合占了，新的那条至少要换掉其中一项。
 *
 * 6. **品类要散开，别整档围着同一个品类转**（2026-10-05）。用户两句话把这条钉死：
 *    先是「不要只有衣服，普通商品也需要」—— 短视频档原来七条封面与分镜全围着服装
 *    模特转，卖充电器、沥水垫、收纳盒的人进来一张能直接套的都没有；补了普通商品
 *    之后用户又补了第二句「衣服的类型少点」，因为那时服装仍占七条里的六条。
 *    现在的配比 = **服装 3 条 + 通用结构 1 条 + 普通商品 3 条 + 转台展示 1 条**：
 *      · `vt-01 / vt-03 / vt-06` —— 服装只留三条，判据是**成交路径互不重叠**
 *        （卡点节奏 / 单品质感 / UGC 真实感）。删掉的三条不是不好，是路径重复：
 *        `vt-02 地铁换装` 与 `vt-01 4 LOOK` 都是「多造型换装」，
 *        `vt-04 镜子卡点` 与 `vt-06 通勤风穿搭` 都是「UGC 真实感」，
 *        `vt-05 5 角度看版型` 与 `vt-03 单衣细节` 都是「单品质感展示」。
 *      · `vt-07` —— 不挑品类的 15 秒成交结构；
 *      · `vt-08..vt-10` —— 普通商品，分镜里不出现「衣摆 / 面料 / 版型 / 上身」这类
 *        服装专有动作，换成外壳高光 / 接口特写 / 按压开合 / 使用结果这类**任何商品
 *        都拍得出来**的镜头；
 *      · `mv-02` —— **360° 转台展示**，2026-10-05 并档时从原「商品视频」档留下来的
 *        唯一一条（另三条 白底 / 场景 / 细节 与 `vt-08`、`vt-10` 是同一类拍法，
 *        判定见那条自己的注释）。留下它是因为别的模板都给不出「匀速转满一圈」。
 *    加模板前先问两句：① 这条换到别的品类上还成立吗？② 它与同档已有一条的
 *    **成交路径**是不是同一条？有一问答不上来就别加。
 *
 * ---------------------------------------------------------------------------
 * 封面图
 * ---------------------------------------------------------------------------
 * `public/templates/<id>.webp`，**真实摄影质感**（不是插画）。
 * 生图清单见 `scripts/template-assets.json`（提示词真源），批量生成与落盘见
 * `scripts/gen-template-assets.py` —— 它逐条调 `~/.workbuddy/skills/duomi-image-2/generate.py`
 * （`--no-poll` 提交 / `--batch-poll` 轮询），自己只负责批量驱动与压成 webp。
 * 改图不要手搓、也不要直接在对话里敲提示词：走脚本，
 * 否则几十条的提示词会散在对话里，下次要重做谁都不知道原来写的是什么。
 * **这里不写条数** —— 条数会变，写死就会过期（`export-template-count.ts` 现算）。
 */

import type { CustomVideoTemplate } from '@/api/types'
import type { TemplateItem } from './catalog'
import { isStoryboardShots, videoStoryboard, type Storyboard } from './prompts'
import {
  DEFAULT_VIDEO_MODE,
  VIDEO_DURATIONS,
  VIDEO_RATIOS,
  VIDEO_STYLES,
  type VideoModeId,
  type VideoRatio,
  type VideoStyle,
} from './videoModes'

export type VideoTemplatePreset = {
  /** 必须落在所属档位的 `scenes` 清单里，否则页面 chips 一个都不高亮 */
  scene: string
  style: VideoStyle
  /** 按秒计价，跟着模板换 —— 只换一半会出现「模板是 10 秒、时长却是 15 秒」的混搭 */
  duration: number
  aspectRatio: VideoRatio
}

export type VideoTemplate = {
  id: string
  /** 卡面角标话题（卡点换装 / 都市通勤 / 单品卡点 …） */
  tag: string
  /** 卡面主标题，与 tag 拼成「4 LOOK 极速闪切」 */
  name: string
  /**
   * 角标播放量（如 `92w`）。**只作参考站的传播量展示，不是我们的数据** ——
   * 留空表示这条不是「爆款同款」而是功能性模板（商品视频那四条），
   * 页面上不渲染角标，免得给功能性模板硬安一个假播放量。
   */
  views?: string
  /** 封面：/templates/<id>.webp，真实摄影 */
  cover: string
  /** 一句话说清「选它你会得到什么」，进 title 悬停 */
  desc: string
  /**
   * 归属档位。2026-10-05 并档后只剩 `short-video` 一个值（复刻档不挂模板）——
   * 但这一层**不删**：它是「这条模板属于哪一档」的唯一声明，
   * 以后真要再分档，加一个值就够，页面不用改。
   */
  modes: VideoModeId[]
  /** 选中后整组铺开的参数 */
  preset: VideoTemplatePreset
  /**
   * 这条模板**建议配哪个生成引擎**（模型族 id，见 `config/modelFamilies.ts`）。
   *
   * ## 为什么模板要管引擎（2026-10-10）
   *
   * 起因是「默认档是全场最贵的」：页面默认引擎换成了便宜的 `google-omni`
   * （¥0.15/秒，见 `DEFAULT_FAMILY_ID.video` 的注释），但那一档**只出 6/8/10 秒、
   * 只有 16:9 与 9:16** —— 15 秒的 `vt-07` 或方版 1:1 的 `mv-02` 点在它下面
   * 会当场被时长控件夹回 10 秒，卡片随即脱钩（用户看到的是「点了没反应」）。
   * 所以「模板」与「引擎」不能各挑各的：**这条模板的时长与画幅决定了谁能跑它**。
   *
   * ## 取值规则（冒烟钉着，见 `smoke-video-templates.ts` 的第 ⑦ 条）
   *
   * 必须是「能力上真的能出这条模板」的模型：它的
   * `capabilities.duration_options`（有值时）必须含 `preset.duration`，
   * 否则 `duration_min / duration_max` 必须覆盖它；有 `aspect_ratios` 时
   * 必须含 `preset.aspectRatio`。写错一个词的后果不是编译错，
   * 而是「用户点开模板就看见时长被改掉 / 提交才被上游拒」。
   *
   * **留空 = 不改引擎**（自存模板就是这样：它的参数是用户自己那套，
   * 我们不该替他改通道）。留空时沿用当前引擎。
   */
  recommendedModel?: string
  /**
   * 这条模板**自己的**镜头脚本。两种形态二选一（见 `prompts.ts` 的 `Storyboard`）：
   *
   * - **三段式**（`open / body / close`）：时间轴由 `videoStoryboard(duration)`
   *   按「2 秒 / 60% / 余下」算，这里只提供每一段拍什么。玩法型模板用这个就够。
   * - **多镜头**（`{ shots }`）：每个镜头自带时间段位，用于**结构完整的带货脚本**
   *   （15 秒 6 镜头：钩子 → 产品外观 → 使用动作 → 细节特写 → 场景 → CTA）。
   *   三段式给不出「细节特写只占 2 秒」这种节奏，硬塞会糊成一句。
   *
   * ⚠️ **必填**，不是可选。2026-09-29 实测：只铺参数的话，6 条短视频模板的
   * 提示词除「场景类型 / 风格 / 运镜」三个词与一句【补充】外**逐字相同**，
   * 连【镜头脚本】都是同一份通用商品视频叙事 ——
   * 「学功夫练武术舞蹈」运镜选了**固定机位**，分镜却写「使用过程实拍，手持运镜」；
   * 「实景动画」要求街道空镜开场、产品末段才插入，分镜却让它「0-2s 产品定格特写」。
   * （那批模板 2026-09-30 已整体换掉，这两句留作踩坑记录 —— 现在六条都自带分镜。）
   * 模板只改三个下拉框 = 用户换了个名字，画面一模一样。
   *
   * 必填是为了让 tsc 拦住「新加一条模板忘了写分镜」—— 可选字段必然有人不写。
   *
   * 写的时候守两条：
   * ① 不要出现别条模板的招牌动作（比如每条都「产品定格特写开场」），
   *    那正是上一版栽的地方；
   * ② **多镜头形态下，时间段位必须首尾相接、加总等于 `preset.duration`**
   *    （`smoke-prompts` 会逐条核对，对不上就是「脚本说自己 16 秒」的假模板）。
   */
  storyboard: Storyboard
  /**
   * 跨全片的**结构约束**，落到提示词的【补充】段。
   *
   * 与 `storyboard` 的分工：`storyboard` 是**分段**拍什么（进【镜头脚本】，
   * 右栏那张「分镜脚本」卡片显示的就是它）；`shot` 是整条片子的硬约束 ——
   * 「转速恒定」「两次变装只换服装」「不要黑帧」这类**跨段**要求。
   * 两者互补，不重复：`shot` 里不要再复述分段内容。
   */
  shot: string
}

export const VIDEO_TEMPLATES: VideoTemplate[] = [
  /* ----------------------------- 带货短视频 ----------------------------- */
  {
    /*
     * 2026-09-30 第二批：前六条**整体换掉**。
     *
     * 用户原话「这个不实在了，头几个模板都要换一下，看看我之前带货视频的模板」——
     * 上一版这六条是从参考站搬来的**玩法模板**（会动的服装 / 武术舞蹈 / 3D 地图 /
     * 卡点舞 / 红绿灯变装 / 实景动画）：拍成什么样子各有招牌，但没有一条讲
     * 「这条片子怎么把东西卖出去」，用户看完的判词是「不实在」。
     *
     * 换上去的六条全部取自他自己跑过、有落地成片为证的带货模板：
     * `video-beat-skill`（卡点系：4 LOOK 闪切 / 地铁换装 / 单衣卡点 / 镜子卡点 /
     * 多角度展示）与 `video-UGC-Lifestyle-skill`（UGC 系：通勤风穿搭）。
     *
     * 2026-10-05 精简：换上去的六条里只留了三条 —— 用户看过一轮之后判
     * 「衣服的类型少点」，删的是**路径重复**的三条（vt-02 地铁换装 /
     * vt-04 镜子卡点 / vt-05 5 角度看版型，判定见头部硬约束 6），
     * 留下卡点 / 单品 / UGC 三条各走一条路。**不要再照旧清单往回加。**
     *
     * ⚠️ 与上一批一样：**短视频档一律不写 `views`**。原来的 92w / 102w / 200w 是
     * 「参考站上的传播量」，换成我们自己的模板之后就再无外部数据可引，
     * 继续挂着等于往卡片上放假指标。
     */
    id: 'vt-01',
    tag: '卡点换装',
    name: '4 LOOK 极速闪切',
    cover: '/templates/vt-01.webp',
    desc: '四套造型按重拍硬切轮播，闭场回闪翻倍信息密度，完播率靠节奏拿',
    modes: ['short-video'],
    preset: { scene: '种草氛围', style: '高对比潮流', duration: 8, aspectRatio: '9:16' },
    /* 8 秒竖版 —— 便宜档（¥0.15/秒）就出得了，全片 ¥1.20。
       下面的 15 秒与 1:1 那几条**不能**写它（它只出 6/8/10 秒、只有 16:9 / 9:16），
       各自写 `youkou-wan-3-0`（清单里最便宜的一档「全能力」模型）。 */
    recommendedModel: 'google-omni',
    storyboard: {
      shots: [
        { t: '0-1s', d: '开场即信号：黑场起手，同一个模特四套造型的定格各闪一帧，四套各由一种主色统领，靠配色与机位差异区分；本段没有人走动' },
        { t: '1-2.5s', d: 'LOOK 01：全身景建立造型，镜头沿人物视线缓慢推近，衣摆与发丝在同一次转身里甩开' },
        { t: '2.5-4s', d: 'LOOK 02：从鞋面与裤脚起手向上摇，收到半身定格；同一模特同一站位，只换整套穿搭' },
        { t: '4-5.5s', d: 'LOOK 03：镜头横移掠过上半身，衣物面料在移动中出现真实褶皱与垂坠，人物保持侧身' },
        { t: '5.5-7s', d: 'LOOK 04：慢速推近到胸口以上，人物抬眼看向镜头，造型以最完整的一面停住' },
        { t: '7-8s', d: '闭场回闪：四套造型按一、二、三、四的顺序往返各闪一帧，最后停在完整全身，画面下方保持干净' },
      ],
    },
    shot: '节拍是硬约束：每套造型的切换必须落在同一组重拍上，中途不许插入渐变或滑动转场；四套造型由同一个模特完成，面部与身形保持一致；每套造型各自的运镜不许重复',
  },
  {
    id: 'vt-03',
    tag: '单品卡点',
    name: '单衣细节闪切',
    cover: '/templates/vt-03.webp',
    desc: '平铺 → 细节 → 上身 → 定格，一件单品的质感与版型一次讲透',
    modes: ['short-video'],
    preset: { scene: '信息流广告', style: '清爽电商棚拍', duration: 8, aspectRatio: '9:16' },
    recommendedModel: 'google-omni',
    storyboard: {
      shots: [
        { t: '0-1s', d: '开场是挂拍：单品挂在木质衣架上轻轻转动半圈，背景干净，这一段没有人入画' },
        { t: '1-2s', d: '领口与门襟的特写，镜头贴着面料平移，走线与锁边清晰可辨' },
        { t: '2-3s', d: '面料微距：布料被指尖轻轻捻起后回弹，织纹与光泽在侧光下显形' },
        { t: '3-4s', d: '平铺全身版型俯拍，袖笼与下摆的自然弧线完整展开' },
        { t: '4-5.5s', d: '上身正面中景，穿着者站定后微微侧转，衣服的肩线与腰线随之变化' },
        { t: '5.5-6.5s', d: '侧身与背面的连续切换，背面设计给出一个完整停留' },
        { t: '6.5-8s', d: '收在全身动态：穿着者向镜头走两步后站定，画面下方保持干净' },
      ],
    },
    shot: '每一个切点只讲一件事，同一处细节不要重复给两次；面料、走线、垂坠这三类质感镜头合计要占到全片一半以上；背景保持同一块干净底，不许出现第二个道具',
  },
  {
    id: 'vt-06',
    tag: 'UGC 种草',
    name: '通勤风穿搭',
    cover: '/templates/vt-06.webp',
    desc: '通勤场景跟拍，无口播、全片无字，靠画面与氛围带出穿搭欲望',
    modes: ['short-video'],
    preset: { scene: '种草氛围', style: '自然光实拍 UGC', duration: 10, aspectRatio: '9:16' },
    recommendedModel: 'google-omni',
    storyboard: {
      shots: [
        { t: '0-2s', d: '清晨的写字楼外，穿着者端着咖啡从旋转门走出，镜头从斜后方跟上半身' },
        { t: '2-4s', d: '沿人行道向前走，镜头贴着侧后方带一点呼吸感，衣摆与包带随步伐轻晃' },
        { t: '4-6s', d: '在路口停下等灯，穿着者低头看了一眼腕表又抬眼看向前方，上半身入画' },
        { t: '6-8s', d: '推开咖啡馆的门进入室内，镜头随人流方向平移，光线从冷调转为暖调' },
        { t: '8-10s', d: '落座后把包放在膝上，抬手理了一下领口，视线望向窗外，画面下方保持干净' },
      ],
    },
    shot: '全片保持自然光与真实场景，不要出现棚拍打光的平硬感；镜头始终以跟拍视角出现，允许轻微呼吸感晃动，但不许出现匀速平移的轨道感；画面里不要出现多余路人与品牌标识',
  },
  {
    /*
     * 2026-09-30 用户原话「你帮我加一个 15s 的脚本模板」，要的就是这条。
     * 前面那批模板各自给了一种拍法或一种节奏，但**没有一条把「这条片子怎么把东西
     * 卖出去」写成结构** —— 这条不挑玩法，给一套**结构完整的成交脚本**。
     *
     * 结构取自悟空 Seedance 那套 15 秒带货分镜（`category-general` 的通用兜底：
     * 钩子 → 产品外观 → 使用动作 → 细节证明 → 场景 → CTA），六个镜头的时间段位
     * 逐条对齐 —— 它的「细节特写」只占 2 秒，这正是三段式给不出的节奏。
     *
     * ⚠️ **`preset` 必须与同档其他条逐字段不全等**：选中态是 `activeTemplate()`
     * 拿表单和模板的五个字段全等比出来的，撞一条就有一条永远点不亮（用户看到的是
     * 「点了没反应」）。所以场景挑了目前**零模板占用**的「模特手持产品」，
     * 风格也挑了短视频档没用过的「清爽电商棚拍」。
     *
     * ⚠️ **不写 `views`**。角标标的是「参考站上的传播量」，这条是我们自己
     * 按专业脚本结构写的，没有任何外部数据可引 —— 编一个「150w」就是往卡片上放
     * 假指标（`views` 字段的注释写明了它只作参考站展示）。
     * 2026-09-30 换批之后这条升格为**全档规矩**：短视频档七条一律不写 `views`。
     */
    id: 'vt-07',
    tag: '带货脚本',
    name: '15秒成交结构',
    cover: '/templates/vt-07.webp',
    desc: '6 镜头标准成交结构：钩子 → 产品 → 使用 → 细节 → 场景 → 收尾，15 秒讲完一条完整购买理由',
    modes: ['short-video'],
    preset: { scene: '模特手持产品', style: '清爽电商棚拍', duration: 15, aspectRatio: '9:16' },
    recommendedModel: 'youkou-wan-3-0',
    storyboard: {
      shots: [
        { t: '0-2s', d: '开场即钩子：模特正面半身站在浅灰棚拍底前，双手空空、摊手看向镜头，一副「又碰上这个麻烦」的表情；画面干净留白，产品这一段不要入画' },
        { t: '2-5s', d: '产品从画面下缘被双手端进画，举到胸前正面朝向镜头；镜头同步缓慢推近，产品占到画面上半部' },
        { t: '5-8s', d: '真实使用动作一次做完：单手托住产品，另一只手完成一个明确的使用动作并停在完成的一瞬；镜头保持不动，动作不要重复第二遍' },
        { t: '8-10s', d: '镜头推到最近，产品表面细节占满画幅，材质与做工的纹理清晰可辨；这一段是全片最短的一段，停满即走' },
        { t: '10-13s', d: '镜头匀速拉回中景，模特把产品端在胸前一侧，另一只手指向它，视线从产品转向镜头' },
        { t: '13-15s', d: '产品停在画面中央，手稳定托住不再动；画面下方保持干净' },
      ],
    },
    shot: '全片守一块浅灰棚拍底与同一套光线色温；画面里只有一位模特和一件产品，不加第二个道具或第二个人；机位只做匀速推拉，画面不抖不甩；产品外观（形状 / 颜色 / 包装）全程不得改动',
  },

  /* ------------------- 普通商品（2026-10-05 新增三条） ------------------- */
  {
    /*
     * 用户原话：「不要只有衣服，普通商品也需要」。
     *
     * 前七条虽然质量不差，但它们**全是服装的拍法**：四套造型、地铁换装、面料微距、
     * 上身试穿、走版型 —— 做 3C 数码 / 家居日用的人进来，一张能直接套的都没有。
     * 这三条是**加法**，一条服装模板都没动。
     *
     * 场景刻意挑了短视频档**零占用**的三个（TVC 品牌质 / 纯带货口播 / 模特使用产品），
     * 所以这组参数天然与老七条不全等（见本文件头部硬约束 5）。
     *
     * 三条各自的成交逻辑不一样，不是同一个脚本换三个名字：
     *   · `vt-08` 靠**产品本身**成交 —— 客单价高的数码硬货，先让人相信做工；
     *   · `vt-09` 靠**讲解密度**成交 —— 低客单、决策快的日用百货，把卖点一条条说完；
     *   · `vt-10` 靠**看得见的结果**成交 —— 家居品类，先演一遍再谈卖点。
     *
     * 三条都**不写 `views`**（短视频档七条之后立的规矩：编一个播放量就是放假指标）。
     */
    id: 'vt-08',
    tag: '品牌大片',
    name: '3C 数码硬核开场',
    cover: '/templates/vt-08.webp',
    desc: '连续推近把产品当主角拍，一条片子讲清工业设计，适合客单价高、要建立信任的数码硬货',
    modes: ['short-video'],
    preset: { scene: 'TVC 品牌质', style: '极简白底科技感', duration: 15, aspectRatio: '9:16' },
    recommendedModel: 'youkou-wan-3-0',
    storyboard: {
      shots: [
        { t: '0-2s', d: '开场先给纵深：浅灰无缝底一路推到底，画面正中只留产品本体和它自己那圈柔和投影，这一段没有人物、也没有第二件道具' },
        { t: '2-4.5s', d: '镜头沿水平轴向产品缓推，产品以三分之四角静置，外壳材质在单侧硬光下压出一条连续的高光带' },
        { t: '4.5-7s', d: '继续推近到接口与按键区域，金属触点、开模分缝与接缝走线逐一进入景深，这一段画面里只有产品本身' },
        { t: '7-9.5s', d: '产品在画面里占得最大的一刻开始缓慢自转，转轴锁在中心垂线上，四个面依次亮相，每个面停留约半秒' },
        { t: '9.5-12.5s', d: '镜头匀速拉回中景，产品转回正面，底部投影随它离台的高度收放，光位与开场保持一致' },
        { t: '12.5-15s', d: '收在静止的正面：产品居中，四周留白均匀，画面上半部不放任何元素，下方保持干净' },
      ],
    },
    shot: '全片只用一个浅灰无缝底与同一组单侧硬光，色温和光比从头到尾不许变；产品外观（形状 / 配色 / 材质对比）全程不得改动；镜头只在推近与拉远之间变化，不出现摇镜、甩镜与画面抖动；画面里始终只有这一件产品',
  },
  {
    id: 'vt-09',
    tag: '口播带货',
    name: '日用百货卖点直给',
    cover: '/templates/vt-09.webp',
    desc: '真人出镜把卖点一条条讲透，靠讲解密度成交，适合克单价低、决策快的日用百货',
    modes: ['short-video'],
    preset: { scene: '纯带货口播', style: '暖调生活感', duration: 15, aspectRatio: '9:16' },
    recommendedModel: 'youkou-wan-3-0',
    storyboard: {
      shots: [
        { t: '0-2s', d: '出镜者正面半身站在浅色墙面前，直视镜头抛出一句短钩子，两只手自然垂在身体两侧，产品这一段还没进画' },
        { t: '2-4.5s', d: '把产品端到胸口高度、正面朝向镜头，手指避开会挡住关键信息的位置，包装完整入画' },
        { t: '4.5-7s', d: '把产品翻到背面与底部各停一拍，一边说一边用手指点向容量刻度所在的位置' },
        { t: '7-9.5s', d: '用一个真实动作演示它怎么用（按压 / 开合 / 拆装任选一种），动作做完就停在完成的那一瞬，不做第二遍' },
        { t: '9.5-12.5s', d: '台面上并排摆出两个规格做一次对比，手指依次点过每一件，讲到差别的那一下手停住' },
        { t: '12.5-15s', d: '产品回到画面正中央，出镜者双手稳稳托住，视线从产品抬起来看向镜头，下方保持干净' },
      ],
    },
    shot: '机位全程锁死在同一处，出镜者只在画幅内做手势与转身，不许走出画面；念白语速平稳，讲到卖点的那一下手部动作必须同时到位；产品外观与包装保持原样，包装上的印刷内容一律不画；全片只用一处主光，不出现第二个色温与第二个人',
  },
  {
    id: 'vt-10',
    tag: '使用演示',
    name: '家居好物实测',
    cover: '/templates/vt-10.webp',
    desc: '在真实居家环境里演一遍使用全过程，让效果先说话再谈卖点，家居品类最吃这一套',
    modes: ['short-video'],
    preset: { scene: '模特使用产品', style: '自然光实拍 UGC', duration: 10, aspectRatio: '9:16' },
    recommendedModel: 'google-omni',
    storyboard: {
      shots: [
        { t: '0-2s', d: '出镜者从台面上拎起产品往后景的实景区域走，镜头跟在她侧后方，前景还留着上一轮用过的痕迹' },
        { t: '2-4s', d: '在产品要落位的地方停住并把它安置好，镜头随之压低贴近台面，安置动作一次到位、不来回调整' },
        { t: '4-6s', d: '真实使用过程中的中景：水、食材或衣物从产品上方经过，镜头带一点呼吸感轻轻跟住，中途不切走' },
        { t: '6-8s', d: '贴近产品表面看结果，残留的水珠、碎屑或绒毛在表面上留下可辨的痕迹，材质与做工占满画幅' },
        { t: '8-10s', d: '把产品放回它原来的位置，镜头往后拉开到半身，出镜者顺势直起身，下方保持干净' },
      ],
    },
    shot: '全片在同一处真实居家空间里一次拍完，环境里的生活痕迹保留，不要收拾成样板间；镜头始终以跟拍视角出现，允许轻微呼吸感晃动，但不许出现三脚架般的死平画面与推拉轨道感；画面里不出现第二个人、也不出现任何品牌标识',
  },

  /* ---------------- 360° 转台展示（原商品视频档，2026-10-05 并档保留） ---------------- */
  {
    /*
     * 这一条来自原来的「商品视频」档 —— 2026-10-05 两档并成一档时，
     * 那四条里只留下它，另外三条删掉的判据是**成交路径与短视频档重复**：
     *   · `mv-01 白底产品展示` —— 与 `vt-08 3C 数码硬核开场` 是同一类拍法
     *     （浅底 + 产品当主角 + 推近讲做工）；
     *   · `mv-03 生活场景植入` —— 与 `vt-10 家居好物实测` 是同一类
     *     （真实居家 + 完整使用过程）；
     *   · `mv-04 细节推近特写` —— 与 `vt-08` 的「接口微距」段、`vt-03` 的
     *     面料微距段重复。
     * 留下它是因为**别的七条都给不出**：一台匀速转台把产品转满一圈。
     * 三段式带货脚本排不出这个节奏，服装那三条也做不了 —— 它服务的是
     * 「买家想先把产品六个面看清楚」这个独立需求。
     *
     * ⚠️ 它现在归 `short-video`（合并后唯一的主力档），但 `preset` 仍是当初的
     * **8 秒 / 1:1** —— 这是刻意的，不是漏改：转台展示本来就是方版主图位的活。
     * 模板自带预设会把时长与画幅一起铺过去，所以从合并档点它不会被打包的
     * 15 秒 / 9:16 默认值带偏（见 `defaultsOf` 的注释：换档才换整套默认值，
     * 点模板换的是模板自己那一套）。
     */
    id: 'mv-02',
    tag: '转台展示',
    name: '360° 旋转',
    views: undefined,
    cover: '/templates/mv-02.webp',
    desc: '匀速转台把产品转满一圈，一眼看清结构与各面做工',
    modes: ['short-video'],
    preset: { scene: '360° 旋转展示', style: '清爽电商棚拍', duration: 8, aspectRatio: '1:1' },
    recommendedModel: 'youkou-wan-3-0',
    storyboard: {
      open: '产品置于转台中央，正面朝向镜头，转台开始转动',
      body: '转台匀速转满一整圈（8 秒 360°），机位固定不动；棚拍硬光 + 柔光箱组合，金属面保留高光条',
      close: '转台转回正面停稳收尾，产品居中；背景保持纯色不变',
    },
    shot: '转速全程恒定，不要忽快忽慢；一圈刚好转满 360°，不多转也不少转',
  },

  /* ------------- 补库五条（2026-10-09：填满 12 个场景的五个空档） ------------- */
  {
    /*
     * 用户判词「模板我感觉不太够 / 视频生成不够多样」之后的补库（清单见
     * `docs/视频模板-候选清单.md`）。这五条**唯一的目标就是填场景空缺** ——
     * 短视频档的 12 个场景里，原先只有 7 个有模板承载，下面五条各接一个
     * （UGC 软广 / 促销视频 / 场景展示 / 细节推近 / 产品展示）。
     *
     * ⚠️ 每条的成交路径与相邻模板**刻意错开**，不是同一脚本换名字（判据见头部硬约束 6）：
     *   · `vt-11` 靠效果对比（B&A），不是 vt-06/vt-10 的「氛围好看 / 怎么用」；
     *   · `vt-12` 靠利益点（给得多），不是 vt-09 的讲解密度；
     *   · `vt-13` 落在**关系**上，不是 vt-10 的产品效果；
     *   · `vt-14` 以**质感特写**为主体，现有八条一条都没有这种主体；
     *   · `vt-15` 的画面主体是**一组**，卖「齐不齐」，现有条全卖单品。
     */
    id: 'vt-11',
    tag: '素人验效',
    name: '前后对比实测',
    cover: '/templates/vt-11.webp',
    desc: '同一机位拍「用前 / 用后」，靠效果对比成交，美妆个护与洗护清洁最吃这一套',
    modes: ['short-video'],
    preset: { scene: 'UGC 软广', style: '自然光实拍 UGC', duration: 10, aspectRatio: '9:16' },
    recommendedModel: 'google-omni',
    storyboard: {
      shots: [
        { t: '0-2s', d: '问题状态特写：只拍问题本身（干燥 / 泛红 / 污渍），机位钉死不动，画面下方留出干净的一条' },
        { t: '2-4.5s', d: '一只手从画面下缘把产品端进画，停在画面中部，产品正面朝向镜头，机位仍不动' },
        { t: '4.5-7.5s', d: '在同一构图里完成一次明确的使用动作，动作一次做完、不重复第二遍' },
        { t: '7.5-10s', d: '撤掉产品，画面回到第一个镜头的同一位置，只留结果状态，停满即走' },
      ],
    },
    shot: '前后两个镜头必须同机位、同构图、同光位，唯一变化的是被拍对象的状态；全程机位不许移动也不许变焦；画面里只有一只入画的手，不出现人脸、不出现第二个道具',
  },
  {
    id: 'vt-12',
    tag: '大促直给',
    name: '一份钱给多少',
    cover: '/templates/vt-12.webp',
    desc: '一整套一次摆开、逐件亮相，靠「给得多」的画面量感成交，食品饮料与囤货型日用适用',
    modes: ['short-video'],
    preset: { scene: '促销视频', style: '高对比潮流', duration: 8, aspectRatio: '9:16' },
    recommendedModel: 'google-omni',
    storyboard: {
      open: '量感开场：一整套东西在同一平面上一次摆开、全部入画，稍高角度俯拍，件数一眼可数',
      body: '逐件亮相：机位不动，每件被拿起再放回原位，每次都放回同一个位置，靠位置不变堆出量感',
      close: '收口：画面收在完整一套的正面，所有件都在画内，留白干净，停满即走',
    },
    shot: '利益点只靠画面对比表达（摆开的件数、赠品同框的体积差、拆箱后的满桌状态），全程不上任何价格与促销信息；机位全程不动；出现的东西必须是同一套里真实包含的件数，不许为了堆量反复摆入同一件',
  },
  {
    id: 'vt-13',
    tag: '场景代入',
    name: '母婴宠物日常',
    cover: '/templates/vt-13.webp',
    desc: '在真实居家日常里演一段照顾关系，靠人与物的互动带出代入感，母婴与宠物品类适用',
    modes: ['short-video'],
    preset: { scene: '场景展示', style: '暖调生活感', duration: 15, aspectRatio: '9:16' },
    recommendedModel: 'youkou-wan-3-0',
    storyboard: {
      shots: [
        { t: '0-2.5s', d: '清晨居家开场：自然光从窗户进来，镜头带轻微呼吸感，人物还没开始用产品' },
        { t: '2.5-5s', d: '使用动作：一只手扶着、另一只手操作的忙碌真实感，允许动作不完美' },
        { t: '5-7.5s', d: '被照顾对象的反应：宝宝笑或宠物主动凑过来，这是全片最重要的一段，给足停留' },
        { t: '7.5-10s', d: '镜头稍微收近，让产品与它的使用痕迹同框' },
        { t: '10-12.5s', d: '收拾与收纳：用完归位的日常动作，背景里保留家的其他痕迹' },
        { t: '12.5-15s', d: '收在人与物一起的画面：不摆正、不看镜头，停满即走' },
      ],
    },
    shot: '落点是关系而不是产品：画面里必须有人（或宠物）的互动反应，产品只是这件事的一部分；全片在同一处真实居家空间一次拍完，保留生活痕迹、不收拾成样板间；跟拍视角允许轻微呼吸感，但不许出现三脚架般的死平画面；除被照顾对象外不出现第三个人',
  },
  {
    id: 'vt-14',
    tag: '工艺微距',
    name: '材质与做工',
    cover: '/templates/vt-14.webp',
    desc: '以质感特写为主体，让材料表面与工艺接缝自己说话，适合茶器、饰品与皮具五金',
    modes: ['short-video'],
    preset: { scene: '细节推近', style: '清爽电商棚拍', duration: 10, aspectRatio: '9:16' },
    recommendedModel: 'google-omni',
    storyboard: {
      shots: [
        { t: '0-2s', d: '材料表面微距：镜头贴着表面缓慢平移，纹理在侧光下显形' },
        { t: '2-4s', d: '推到缝线 / 拼接 / 打磨处，手工痕迹清晰可辨' },
        { t: '4-6s', d: '镜头绕器物转过一个角度，让一条高光沿边缘完整走一遍' },
        { t: '6-8s', d: '指尖入画轻触后离开，留下一个可感知的回弹或温润' },
        { t: '8-10s', d: '镜头拉回中景，完整器物在极简背景里停住' },
      ],
    },
    shot: '镜头只在推近与拉回之间变化，不出现摇镜与甩镜；全片只用同一组侧光，让质感在同一个光位下前后一致；画面里只有这一件器物，不出现包装、标签与第二件道具',
  },
  {
    id: 'vt-15',
    tag: '套系陈列',
    name: '一整套摆开',
    cover: '/templates/vt-15.webp',
    desc: '俯拍把整套一次摆开、逐件揭开，卖的是「齐不齐、怎么配」，礼盒与多件套适用',
    modes: ['short-video'],
    preset: { scene: '产品展示', style: '暖调生活感', duration: 8, aspectRatio: '1:1' },
    recommendedModel: 'youkou-wan-3-0',
    storyboard: {
      shots: [
        { t: '0-2s', d: '俯拍全景：整套一次摆开、件数一眼可数，构图对称' },
        { t: '2-4s', d: '逐件揭开：盖子 / 盒盖被依次打开，每件露出本体，机位不动' },
        { t: '4-6s', d: '合体使用：几件东西被组合到一起，展示它们之间怎么配合' },
        { t: '6-8s', d: '收口定格：回到完整套装正面，所有件都在画内' },
      ],
    },
    shot: '画面主体是「一组」而不是单品，件数与真实套装完全一致；机位全程不动、景别不跳；背景保持同一块干净桌面与同一套暖光，不加第二组道具',
  },
]

/* -------------------------------------------------------------------------- */
/* 用户自存模板（2026-10-08）                                                  */
/* -------------------------------------------------------------------------- */

/**
 * 归一后的模板 —— **内置与自存到了这一层就没有区别了**。
 *
 * 页面（模板条、模板中心、预览抽屉、`activeTemplate`）只认这一个形状，
 * 各自去分辨「这条是内置还是自存」的话，「自存模板缺一个字段」这类问题
 * 就会在四个地方各表现一次，而且各有各的症状。
 */
export type AnyVideoTemplate = VideoTemplate & {
  /**
   * 来源。两个消费方：
   *   · 模板中心靠它把卡片归到「我的模板」那一节并渲染删除键；
   *   · 预览抽屉靠它**跳过案例片探活**（自存模板没有 `sampleUrl` 时不该去 HEAD
   *     `/templates/<id>.mp4` —— 那个文件只给内置模板生成，必然 404，见 `TemplatePreview`）。
   * 工作台的卡片渲染**不比这个字段** —— 卡片长得一样、行为一样，
   * 在那儿分叉只会让用户以为自定义模板是「另一种东西」。
   */
  custom?: boolean
  /**
   * 这条模板真正产出的成片（自存模板才有）。
   * 预览抽屉优先播它；没有就回落「逐格播分镜」（与内置模板同一套回落）。
   */
  sampleUrl?: string
}

/**
 * 自存模板 → 统一形状。**字段一个都不能省**。
 *
 * 最容易漏的是 `storyboard`：只铺参数不铺分镜的模板，用户点它会看到
 * 「场景/风格/时长都对了，但分镜还是上一条产品的」—— 而参数全对，
 * 看上去不像坏了。所以这里显式带上，并在注释里钉住。
 */
export function toAnyTemplate(c: CustomVideoTemplate): AnyVideoTemplate {
  const cover = c.coverUrl || c.coverLocal || ''
  return {
    id: c.id,
    /*
      角标**不再兜底成「我的模板」**（2026-10-08 改）。
      原来兜为「我的模板」是为了躲开 `templateTitle()` 拼出「 · 某某」那个孤零零的
      分隔点 —— 但工作台的模板卡只有 **84px 宽**，那样一来每条自存模板都白吃掉
      「我的模板 · 」这 7 个字符，真正能区分它们的那截名字反而被两行截断掉。
      现在改用**正解**：`templateTitle()` 自己认空角标（见那边的注释），
      所以这里原样透传，用户填了才显示。
    */
    tag: c.tag,
    name: c.name,
    cover,
    desc: c.description || '自己存的模板',
    modes: (c.modes as VideoModeId[]) ?? ['short-video'],
    /*
      ⚠️ 这里的 `as` 是**有意**的边界转换，不是偷懒：`api/types.ts` 的 preset 是
      `{scene: string; style: string; duration: number; aspectRatio: string}`
      —— `api/` 不许反向依赖 `config/`（所以那边只能用宽类型），
      而 `config/` 是允许依赖 `api/` 的，跨过去这一层就该收窄。
      值本身是安全的：写入侧 `VideoStudio.saveTemplate()` 送的就是 `form.*`，
      而 `form` 的那几个字段正是 `VideoStyle` / `VideoRatio`。
      ⚠️ 已知限制（与 `useDraft` 的 `{version, migrate}` 是同一类问题）：
      将来若**改名 `videoModes.ts` 里的枚举值**，旧模板存的还是老字符串 ——
      那时页面上的 chip 会一个都不高亮、而提示词里仍带着老词。
      为此真要改枚举时，必须同时给自存模板加一次迁移。
    */
    preset: c.preset as VideoTemplatePreset,
    storyboard: c.storyboard as unknown as Storyboard,
    shot: c.shot,
    custom: true,
    sampleUrl: c.sampleUrl || undefined,
  }
}

/**
 * 自存模板 → 可编辑的镜头行（`{t, d}[]`）—— 「编辑模板」弹窗里那两块内容的初值。
 *
 * ## 为什么不直接读 `c.storyboard.shots`
 *
 * 模板的分镜有**两种形态**（多镜头 `{shots}` / 三段式 `{open, body, close}`，
 * 见 `Storyboard`）：三段式的时间轴要按时长现算，`shots` 那个键根本不存在。
 * 这里统一走 `videoStoryboard()` —— 它是**套用这条模板时真正会用的那一份**
 * 算法。各读各的话会出现「弹窗里显示的时间位，和套用后分镜的时间位不一样」，
 * 而两边单看都像是对的（同一类静默不一致，本项目栽过多次）。
 */
export function customTemplateShots(c: CustomVideoTemplate): { t: string; d: string }[] {
  return videoStoryboard(c.preset.duration, c.storyboard as unknown as Storyboard)
}

/**
 * 弹窗交回来的镜头行 → 落库分镜（`{shots}`），**按原形态回写**。
 *
 * 与 `customTemplateShots()` 是一对：那个负责「模板 → 可编辑的行」（读），
 * 这个负责「编辑过的行 → 模板」（写）。两个方向都只此一份实现。
 *
 * ## 为什么不能无条件写 `{ shots }`（2026-10-08 实测踩到）
 *
 * 形态本身**会出现在提示词里**：`buildStoryboardPrompt` 按
 * `isStoryboardShots(i.storyboard)` 选量词 —— 多镜头叫「镜头 1」，
 * 三段式叫「第 1 段」（见 `prompts.ts` 的 `multi`）。
 *
 * 于是「一律存 `{shots}`」等于**每次保存都把三段式模板改写成多镜头**，
 * 而这不是无害的重排：
 *
 * - `activeTemplate()` 是「自存在前 + 参数全等」，`find()` 认第一条
 *   ⇒ 用户点完「存为模板」，**新模板立刻被命中**；
 * - 形态一变，`sbAutoPrompt` 就跟着变 ⇒ `samePrompt(sbDone.prompt, sbPrompt)` 为假
 *   ⇒ `sbFresh` 为假 ⇒ **他刚花掉额度出的那张分镜九宫格被判成「不新鲜」**，
 *   预览消失、徽章翻成「已按当前参数更新」。
 *
 * 而两版**镜头内容逐字相同**，差别只有「第 N 段 / 镜头 N」这个量词 ——
 * 用户什么都没改却丢了张图，是最难自查的一类（界面每一处单看都对）。
 *
 * ⚠️ 时间位 `t` 不参与回写：三段式的 `t` 由时长现算（`videoStoryboard`），
 * 存进库里没有意义 —— 而多镜头形态的 `t` 是模板写死的，必须原样带回去。
 * 所以这里只在**目标形态是三段式且仍是 3 条**时回落三键形态，
 * 其余一律存 `{shots}`：条数对不上时硬塞进 `open/body/close` 会**丢镜头**，
 * 换个形态至少不丢内容。
 *
 * ## 为什么 `original` 为空也算「三段式」
 *
 * 表单没命中任何模板时（`activeTemplate()` 返回 `undefined`），页面上的分镜是
 * `videoStoryboard()` 的兜底 `GENERIC_BEATS` —— **就是三段式**，量词也是「第 N 段」。
 * 若这时存成 `{shots}`，写库那一刻起形态就翻了，症状与上面完全一样。
 * 所以在库里没有对应形态可继承时，判据是「**页面此刻用的是哪个形态**」，
 * 而空模板对应的就是三段式。
 */
export function storyboardWithEditedShots(
  original: Storyboard | Record<string, unknown> | undefined,
  shots: { t: string; d: string }[],
): Storyboard {
  const wasShots = !!original && isStoryboardShots(original as Storyboard)
  if (!wasShots && shots.length === 3) {
    return { open: shots[0].d, body: shots[1].d, close: shots[2].d }
  }
  return { shots }
}

/**
 * 本档可用的模板 = **自存在前 + 内置在后**，页面里不再写第二份 `modes.includes` 判断。
 *
 * ## 为什么自存的排最前面
 *
 * 用户点「存为模板」之后，第一件想看到的事是「我存的那张出现了」。
 * 内置那 8 条已经占满一条 84px 卡片宽的轨道，把自存的接在末尾等于
 * 让他横滑到底才知道存没存上 —— 而它刚存完就在最左边，是零延迟的反馈。
 *
 * ## 为什么传参而不是在这里读全局
 *
 * 自存模板来自后端、是**异步**的，而本文件是纯配置（有一批 smoke 测试直接
 * import 它、不挂 React）。把网络状态塞进来的话，那些测试要么起一个 Provider、
 * 要么就得接受「有时空有时不空」的返回值。所以由调用方（Context）把清单传进来，
 * 这里只负责合并这一件事 —— **合并的规则仍然只有这一份实现**。
 */
export function templatesForMode(
  mode: VideoModeId,
  custom: CustomVideoTemplate[] = [],
): AnyVideoTemplate[] {
  return [...custom.map(toAnyTemplate), ...VIDEO_TEMPLATES].filter((t) => t.modes.includes(mode))
}

/**
 * 当前表单命中的模板 —— **派生，不存**。
 *
 * 逐字段全等才认：用户点完模板又手动把风格改掉，卡片立刻熄掉，
 * 而不是继续亮着假装「你还在用这个模板」（那是在骗人）。
 * 传进来的字段全为空串 / undefined 时必然匹配不上任何模板。
 *
 * ## 参数撞车时谁赢：**自存的**
 *
 * `find()` 认第一条，而 `templatesForMode()` 把自存的排在前面 —— 所以
 * 「用户存了一条与内置模板参数完全相同的模板」时，亮的是他自己那条。
 * 这个方向是我选的，不是碰巧：他刚点过「存为模板」，那一刻最该有反馈的
 * 就是自己刚存的东西。反过来的话，卡片会亮成旁边那条内置模板，
 * 用户的第一反应是「我存的没成」。
 *
 * ⚠️ 已知限制（与内置模板同源、刻意不治）：同一个账号存了**两条参数完全相同**
 * 的模板时，只有靠前那条会亮。参数相同就意味着套用效果相同，
 * 多出来那条只是名字不同 —— 为此加一套「id 优先」的判定，会让
 * 「手改了参数卡片还不熄」的骗人问题重新长回来。
 */
export function activeTemplate(
  mode: VideoModeId,
  form: { scene: string; style: string; duration: number; aspectRatio: string },
  custom: CustomVideoTemplate[] = [],
): AnyVideoTemplate | undefined {
  return templatesForMode(mode, custom).find(
    (t) =>
      t.preset.scene === form.scene &&
      t.preset.style === form.style &&
      t.preset.duration === form.duration &&
      t.preset.aspectRatio === form.aspectRatio,
  )
}

/**
 * 模板卡上的完整标题（`4 LOOK 极速闪切`）—— 列表页与工作台共用同一份拼法。
 *
 * ⚠️ **空角标要认**（2026-10-08）：自存模板的角标默认是空的，因为工作台的模板卡
 * 只有 84px 宽 —— 硬拼成「我的模板 · 4 LOOK 闪切」会白吃掉 7 个字符，
 * 用户真正用来区分自己那几条的名字反而被两行截断。
 *
 * 空角标返回**裸名字**而不是「 · 名字」：后者会在卡面上留下一个孤零零的分隔点，
 * 看着像渲染坏了。这条判据放在这里（唯一的拼法），而不是让调用方各自 `tag || ''` ——
 * 那样又变成两份实现。
 */
export function templateTitle(t: VideoTemplate): string {
  const tag = t.tag.trim()
  return tag ? `${tag} · ${t.name}` : t.name
}

/**
 * 模板 → 提示词的【补充】段。放进 `extra` 而不是新开一个提示词变量，
 * 因为 `buildVideoPrompt` 已经有【补充】这一行且原样透传。
 *
 * ⚠️ 这里只放 `shot`（跨全片的结构约束）。**分镜不从这里走** ——
 * 分镜是有时间轴的结构化数据，走 `VideoInput.storyboard` 进【镜头脚本】段；
 * 塞进【补充】会变成一行长句，右栏那张「分镜脚本」卡片也就没法复用它。
 */
export function templateShotLine(t?: VideoTemplate): string {
  return t ? `【模板】${templateTitle(t)} —— ${t.shot}` : ''
}

/**
 * 自存模板 → **模板中心**的卡片条目（`catalog.TemplateItem`）。
 *
 * ## 为什么这个映射在 `videoTemplates.ts` 而不在 `catalog.ts`
 *
 * 它要调 `templateTitle()` / `toAnyTemplate()`，而 `catalog.ts` 一旦 import
 * 本文件就成环（`videoTemplates → prompts → catalog`）。反过来是安全的：
 * 本文件 import `catalog` 只是多一条单向边，没有回到自己的路径。
 *
 * ## 落地地址为什么必须带 `mode`
 *
 * `?vtpl=<id>` 要靠**当前档位的模板表**才找得到这条模板（`templatesForMode`
 * 按 `modes.includes(mode)` 过滤）。不带 `mode` 的话，用户上次停在哪一档
 * 就按哪一档找 —— 存的是 `short-video` 的模板、人却在别的档，点「使用模板」
 * 会像什么都没发生（页面跳过去了、模板没铺开）。
 *
 * `mode` 取 `c.modes[0]`，与本文件其它地方的 `modes` 语义一致：
 * 第一条就是主档位。空数组（理论上不该出现）退回默认档，不抛错 ——
 * 一条模板的落地地址不该因为脏数据把整个模板中心弄崩。
 */
export function customToTemplateItem(c: CustomVideoTemplate): TemplateItem {
  const t = toAnyTemplate(c)
  return {
    id: c.id,
    name: templateTitle(t),
    description: t.desc,
    category: 'MY_TPL',
    to: `/video-studio?mode=${c.modes[0] || DEFAULT_VIDEO_MODE}&vtpl=${encodeURIComponent(c.id)}`,
    cover: t.cover,
    custom: true,
  }
}
